import { type AccountOpeningOperationRuns } from '../../../models/AccountOpening'
import { type AccountOpeningCollection, type AccountOpeningConfirmScope, type AccountOpeningStepId } from '../../../models/AccountOpeningDescriptor'
import { type ResolvedItem } from './collections'
import { type SubmissionOutcome } from './connectorResult'
import { fillFromContact, type ContactFacts } from './contactFallback'
import { reconcileParties } from './parties'
import { mergePrefilled } from './schemaValues'
import { type ItemPatch, type WizardAccount, type WizardHolder, type WizardParty } from './types'

interface ConfirmedSnapshot {
  stepId: AccountOpeningStepId
  accounts: WizardAccount[]
}

export interface WizardDraft {
  openingId: string | null
  accounts: WizardAccount[]
  parties: WizardParty[]
  confirmations: Record<AccountOpeningStepId, string[]>
  operationResults: Record<AccountOpeningStepId, SubmissionOutcome>
  operationRuns: AccountOpeningOperationRuns
  savedMetadata: Record<string, unknown>
  confirmedSnapshot: ConfirmedSnapshot | null
  readOnly: boolean
}

export type LoadedDraftState = Omit<WizardDraft, 'confirmedSnapshot' | 'readOnly' | 'openingId'> & { openingId: string }

export interface FetchedSchema {
  schema: Record<string, unknown>
  fetchedAt: string
}

export type DraftAction =
  | { type: 'reset' }
  | { type: 'started'; accounts: WizardAccount[] }
  | { type: 'loaded'; loaded: LoadedDraftState; readOnly: boolean }
  | { type: 'itemEdited'; item: ResolvedItem; patch: ItemPatch; scopes: Record<AccountOpeningStepId, AccountOpeningConfirmScope | undefined> }
  | { type: 'intakeApplied'; accounts: WizardAccount[] }
  | { type: 'stepConfirmed'; stepId: AccountOpeningStepId; refs: string[]; snapshot: boolean }
  | { type: 'stepChangesDiscarded'; stepId: AccountOpeningStepId }
  | { type: 'itemConfirmed'; stepId: AccountOpeningStepId; ref: string }
  | { type: 'operationRecorded'; stepId: AccountOpeningStepId; outcome: SubmissionOutcome; at: string }
  | { type: 'schemasFetched'; collection: AccountOpeningCollection; schemas: Map<string, FetchedSchema> }
  | { type: 'valuesFilled'; collection: AccountOpeningCollection; values: Map<string, Record<string, unknown>>; contactFacts?: Map<string, ContactFacts> }
  | { type: 'savedAs'; openingId: string; savedMetadata: Record<string, unknown> }

export const EMPTY_DRAFT: WizardDraft = {
  openingId: null,
  accounts: [],
  parties: [],
  confirmations: {},
  operationResults: {},
  operationRuns: {},
  savedMetadata: {},
  confirmedSnapshot: null,
  readOnly: false,
}

const withAccounts = (draft: WizardDraft, accounts: WizardAccount[]): WizardDraft => ({
  ...draft,
  accounts,
  parties: reconcileParties(accounts, draft.parties),
})

const reconciled = (draft: WizardDraft): WizardDraft => withAccounts(draft, draft.accounts)

const startedDraft = (accounts: WizardAccount[]): WizardDraft => ({ ...EMPTY_DRAFT, accounts, parties: reconcileParties(accounts, []) })

const sameHolder = (holder: WizardHolder, other: WizardHolder): boolean =>
  holder.roleCode === other.roleCode && holder.name === other.name && holder.contactId === other.contactId && holder.relationship === other.relationship

const sameHolders = (holders: WizardHolder[], others: WizardHolder[]): boolean =>
  holders.length === others.length && holders.every((holder, index) => sameHolder(holder, others[index]))

const changedAccountRefs = (previous: WizardAccount[], next: WizardAccount[]): string[] =>
  next
    .filter((account) => {
      const before = previous.find((candidate) => candidate.accountRefId === account.accountRefId)
      return !before || before.typeId !== account.typeId || !sameHolders(before.holders, account.holders)
    })
    .map((account) => account.accountRefId)

// A different account type answers a different schema, so the fetched snapshot and its answers no longer apply.
const editedAccount = (account: WizardAccount, patch: ItemPatch): WizardAccount => {
  const merged = { ...account, ...patch }
  return patch.typeId !== undefined && patch.typeId !== account.typeId ? { ...merged, schemaSnapshot: {}, schemaFetchedAt: undefined, values: {} } : merged
}

const withAccountsApplied = (
  draft: WizardDraft,
  accounts: WizardAccount[],
  options: { withdrawRefs?: string[]; confirmStepId?: AccountOpeningStepId },
): WizardDraft => {
  const applied = withAccounts(draft, accounts)
  const liveRefs = accounts.map((account) => account.accountRefId)
  const withdrawn = new Set(options.withdrawRefs ?? [])
  const confirmations: Record<AccountOpeningStepId, string[]> = {}
  Object.entries(draft.confirmations).forEach(([stepId, refs]) => {
    confirmations[stepId] = refs.filter(
      (ref) => !withdrawn.has(ref) && (liveRefs.includes(ref) || applied.parties.some((party) => party.customerRefId === ref)),
    )
  })
  if (options.confirmStepId) {
    confirmations[options.confirmStepId] = liveRefs
  }
  return { ...applied, confirmations, operationResults: withoutOperationResults(draft) }
}

const withoutConfirmations = (
  draft: WizardDraft,
  { item, scopes }: { item: ResolvedItem; scopes: Record<AccountOpeningStepId, AccountOpeningConfirmScope | undefined> },
): Record<AccountOpeningStepId, string[]> => {
  const next = { ...draft.confirmations }
  Object.entries(scopes).forEach(([affectedId, scope]) => {
    next[affectedId] = scope === 'item' ? (draft.confirmations[affectedId] ?? []).filter((ref) => ref !== item.ref) : []
  })
  return next
}

const withoutOperationResults = (draft: WizardDraft): Record<AccountOpeningStepId, SubmissionOutcome> =>
  Object.keys(draft.operationResults).length === 0 ? draft.operationResults : {}

const accountsWithSchemas = (accounts: WizardAccount[], schemas: Map<string, FetchedSchema>): WizardAccount[] =>
  accounts.map((account) => {
    const fetched = Object.keys(account.schemaSnapshot).length > 0 ? undefined : schemas.get(account.accountRefId)
    return fetched ? { ...account, schemaSnapshot: fetched.schema, schemaFetchedAt: fetched.fetchedAt } : account
  })

const partiesWithSchemas = (parties: WizardParty[], schemas: Map<string, FetchedSchema>): WizardParty[] =>
  parties.map((party) => {
    const fetched = Object.keys(party.kycSchemaSnapshot).length > 0 ? undefined : schemas.get(party.customerRefId)
    return fetched ? { ...party, kycSchemaSnapshot: fetched.schema } : party
  })

export const draftReducer = (draft: WizardDraft, action: DraftAction): WizardDraft => {
  switch (action.type) {
    case 'reset':
      return EMPTY_DRAFT

    case 'started':
      return startedDraft(action.accounts)

    case 'loaded':
      return { ...action.loaded, confirmedSnapshot: null, readOnly: action.readOnly }

    case 'itemEdited': {
      const { item, patch } = action
      const edited =
        item.collection === 'accounts'
          ? withAccounts(
              draft,
              draft.accounts.map((account) => (account.accountRefId === item.ref ? editedAccount(account, patch) : account)),
            )
          : {
              ...draft,
              parties: patch.values
                ? draft.parties.map((party) => (party.customerRefId === item.ref ? { ...party, kycValues: patch.values ?? {} } : party))
                : draft.parties,
            }
      return { ...edited, confirmations: withoutConfirmations(draft, action), operationResults: withoutOperationResults(draft) }
    }

    case 'intakeApplied': {
      if (draft.accounts.length === 0) {
        return startedDraft(action.accounts)
      }
      const applied = withAccountsApplied(draft, action.accounts, { withdrawRefs: changedAccountRefs(draft.accounts, action.accounts) })
      return { ...applied, confirmedSnapshot: null }
    }

    case 'stepConfirmed':
      return {
        ...reconciled(draft),
        confirmations: { ...draft.confirmations, [action.stepId]: action.refs },
        confirmedSnapshot: action.snapshot ? { stepId: action.stepId, accounts: draft.accounts } : draft.confirmedSnapshot,
      }

    case 'stepChangesDiscarded': {
      const snapshot = draft.confirmedSnapshot
      if (!snapshot || snapshot.stepId !== action.stepId) {
        return draft
      }
      const restored = draft.accounts.map((account) => {
        const before = snapshot.accounts.find((candidate) => candidate.accountRefId === account.accountRefId)
        return before ? { ...account, selection: before.selection, typeId: before.typeId, holders: before.holders } : account
      })
      return withAccountsApplied(draft, restored, { confirmStepId: action.stepId })
    }

    case 'itemConfirmed': {
      const refs = draft.confirmations[action.stepId] ?? []
      return refs.includes(action.ref) ? draft : { ...draft, confirmations: { ...draft.confirmations, [action.stepId]: [...refs, action.ref] } }
    }

    case 'operationRecorded':
      return {
        ...draft,
        operationResults: { ...draft.operationResults, [action.stepId]: action.outcome },
        operationRuns: { ...draft.operationRuns, [action.stepId]: action.at },
      }

    case 'schemasFetched':
      return action.collection === 'accounts'
        ? { ...draft, accounts: accountsWithSchemas(draft.accounts, action.schemas) }
        : { ...draft, parties: partiesWithSchemas(draft.parties, action.schemas) }

    case 'valuesFilled':
      if (action.collection === 'accounts') {
        return {
          ...draft,
          accounts: draft.accounts.map((account) => {
            const values = mergePrefilled(account.values, action.values.get(account.accountRefId))
            return values === account.values ? account : { ...account, values }
          }),
        }
      }
      return {
        ...draft,
        parties: draft.parties.map((party) => {
          const answered = mergePrefilled(party.kycValues, action.values.get(party.customerRefId))
          const kycValues = fillFromContact(answered, party.kycSchemaSnapshot, action.contactFacts?.get(party.contactId ?? '') ?? {})
          return kycValues === party.kycValues ? party : { ...party, kycValues }
        }),
      }

    case 'savedAs':
      return { ...draft, openingId: action.openingId, savedMetadata: action.savedMetadata }
  }
}
